// Core
//
// Application core functions. Provides function to communicate with the server,
// get language strings for the UI preload images, geolocation, and events
import _ from 'lodash';

const { slice } = Array.prototype;

export const PLATFORMS = {
    ANDROID: 'android',
    FACEBOOK: 'facebook',
    IOS: 'ios',
    KIOSK: 'kiosk',
    MOBILE: 'mobile',
    TABLE: 'table',
    WEB: 'web',
    INSTORE: 'instore',
    PAYATTABLE: 'pay-at-table',
};

const isPlatform = platform => platform === Mobi2Go.app.platform;
export const isAndroidPlatform = () => isPlatform(PLATFORMS.ANDROID);
export const isFacebookPlatform = () => isPlatform(PLATFORMS.FACEBOOK);
export const isIosPlatform = () => isPlatform(PLATFORMS.IOS);
export const isKioskPlatform = () => isPlatform(PLATFORMS.KIOSK);
export const isMobilePlatform = () => isPlatform(PLATFORMS.MOBILE);
export const isTablePlatform = () => isPlatform(PLATFORMS.TABLE);
export const isWebPlatform = () => isPlatform(PLATFORMS.WEB);
export const isInStorePlatform = () => isPlatform(PLATFORMS.INSTORE);
export const isPayAtTablePlatform = () => isPlatform(PLATFORMS.PAYATTABLE);

export default null;

_.extend(Mobi2Go.app, {
    ready: false,
    PLATFORMS,
    // Fired when all scripts have finished loading
    setReady() {
        Mobi2Go.app.ready = true;
        Mobi2Go.app.disable_animation =
            Mobi2Go.helpers.getQueryStringAsObject().disable_animation ===
            'true';
        Mobi2Go.trigger('app', 'ready', Mobi2Go.app.state);
    },

    // Forces loading of a list of images from server.
    preloadImages() {
        let url;
        const images = {};
        if (
            Mobi2Go.config.preload_images !== undefined &&
            Mobi2Go.config.preload_images.length
        ) {
            Mobi2Go.config.preload_images.forEach(() => {
                images[url] = new Image();
                images[url].src =
                    Mobi2Go.app.base_url + Mobi2Go.config.preload_images[url];
            });
        }
    },
    // Geolocation
    position: {
        latitude: null,
        longitude: null,
    },
    isMobileUI() {
        return /mobile/.test(Mobi2Go.app.ui_name);
    },
    platform: null,
    setPlatform(platform) {
        if (_.includes(Object.values(PLATFORMS), platform.toLowerCase())) {
            this.platform = platform.toLowerCase();
        }
        return this.platform;
    },
    getPlatform() {
        if (this.platform) {
            return this.platform;
        }
        const qs_platform = Mobi2Go.helpers.getQueryStringAsObject().platform;

        if (
            qs_platform &&
            _.includes(Object.values(PLATFORMS), qs_platform.toLowerCase())
        ) {
            this.platform = qs_platform.toLowerCase();
        } else {
            this.platform = this.isMobileUI()
                ? PLATFORMS.MOBILE
                : PLATFORMS.WEB;
        }

        return this.platform;
    },
    container_app: null,
    getContainerApp() {
        if (this.container_app !== null) {
            return this.container_app;
        }

        this.container_app =
            Mobi2Go.helpers.getQueryStringAsObject().container_app || '';
        return this.container_app;
    },

    // Switch between desktop and mobile UI
    switchUI() {
        let query_string = window.location.search;
        const current_ui = Mobi2Go.app.ui_name;

        if (query_string) {
            // Strip ui and no_redirect parameters
            const re = /(&|)ui=(mobile|web)(-embed|)(&no_redirect|)/g;
            query_string = query_string.replace(re, '');
            query_string += '&';
        }

        // UI's switch counter-parts
        const map = {
            web: 'mobile',
            'web-embed': 'mobile-embed',
            mobile: 'web',
            'mobile-embed': 'web-embed',
        };

        if (map[current_ui]) {
            window.location.search = `${query_string}ui=${map[current_ui]}&no_redirect`;
        }
    },

    switchLanguage(language) {
        if (language === Mobi2Go.app.language) {
            return;
        }

        let query_string = window.location.search;

        if (query_string) {
            query_string = query_string.replace(
                /&language(=[^&]*)?|^\?language(=[^&]*)?&?/g,
                ''
            );
            if (query_string.length) {
                query_string += '&';
            }
        }

        window.location.search = `${query_string}language=${language}`;
        Mobi2Go.UI.trigger('showLanguagePickerSpinner', language);
    },

    // Mixin event methods. Used to give objects event methods. Returns bind,
    // unbind and trigger methods which are limited to a single channel. Will
    // extend an object with methods if provided
    mixinEvents(channel, object) {
        const events = {
            bind(event_name, fn) {
                Mobi2Go.bind(channel, event_name, fn);
                return object;
            },
            unbind(event_name, fn) {
                Mobi2Go.unbind(channel, event_name, fn);
                return object;
            },
            trigger(...args) {
                if (object && object.mute !== undefined && object.mute)
                    return object;

                const trigger_args = [channel].concat(slice.call(args));
                Mobi2Go.trigger(...trigger_args);
                return object;
            },
            once(event_name, fn) {
                Mobi2Go.once(channel, event_name, fn);
                return object;
            },
        };

        if (typeof object === 'object') {
            _.extend(object, events);
        }

        return events;
    },
    VERSION: 3,
    DATE_FORMAT: 'YYYY-MM-DD',
    DAYS: [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday',
    ],
});
